import { createFileRoute, Link } from "@tanstack/react-router";
import { LegalLayout } from "@/components/legal-layout";

export const Route = createFileRoute("/acceptable-use")({
  component: AcceptableUsePage,
});

function AcceptableUsePage() {
  return (
    <LegalLayout title="Acceptable Use Policy" lastUpdated="December 24, 2025">
      <section className="space-y-6">
        <div>
          <h2 className="text-2xl font-semibold mb-4">1. Introduction</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            This Acceptable Use Policy sets out the standards for using Images
            AI's services. These rules protect our community and ensure the
            platform is used responsibly and legally.
          </p>
          <p className="text-muted-foreground leading-relaxed">
            This policy is part of our{" "}
            <Link to="/terms" className="text-foreground hover:underline">
              Terms of Service
            </Link>
            . Violations may result in content removal, account suspension, or
            termination without refund.
          </p>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">2. Prohibited Content</h2>
          <p className="text-muted-foreground leading-relaxed mb-6">
            You may NOT use our AI image generation or editing services to
            create, upload, or share content that:
          </p>

          <h3 className="text-xl font-semibold mb-3 mt-6">
            2.1 Illegal Content
          </h3>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
            <li>Violates German, EU, or international law</li>
            <li>
              Infringes on intellectual property rights (copyright, trademark,
              patents)
            </li>
            <li>
              Contains child sexual abuse material (CSAM) or child exploitation
            </li>
            <li>Promotes or facilitates terrorism or violent extremism</li>
            <li>
              Facilitates illegal activities (drug trafficking, weapons sales,
              fraud)
            </li>
          </ul>

          <h3 className="text-xl font-semibold mb-3 mt-6">
            2.2 Harmful or Violent Content
          </h3>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
            <li>Depicts graphic violence, gore, or mutilation</li>
            <li>Promotes self-harm, suicide, or eating disorders</li>
            <li>
              Glorifies or encourages violence against individuals or groups
            </li>
            <li>Contains realistic depictions of extreme violence</li>
          </ul>

          <h3 className="text-xl font-semibold mb-3 mt-6">
            2.3 Hateful or Discriminatory Content
          </h3>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
            <li>
              Promotes hatred, discrimination, or violence based on race,
              ethnicity, religion, gender, sexual orientation, disability, or
              other protected characteristics
            </li>
            <li>
              Contains Nazi symbolism or Holocaust denial (illegal in Germany)
            </li>
            <li>Promotes white supremacy, racism, or other hate ideologies</li>
            <li>Harasses, threatens, or bullies individuals or groups</li>
          </ul>

          <h3 className="text-xl font-semibold mb-3 mt-6">
            2.4 Sexual Content
          </h3>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
            <li>Depicts minors in sexual or suggestive contexts</li>
            <li>Non-consensual intimate imagery (revenge porn)</li>
            <li>Pornographic or sexually explicit content</li>
            <li>Sexual exploitation or trafficking</li>
          </ul>

          <h3 className="text-xl font-semibold mb-3 mt-6">
            2.5 Misleading or Deceptive Content
          </h3>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
            <li>
              Deepfakes intended to deceive (manipulated images of real people
              without consent)
            </li>
            <li>Misinformation that could cause public harm</li>
            <li>Impersonation of real individuals or organizations</li>
            <li>Election or voting interference</li>
            <li>Medical misinformation that could cause harm</li>
          </ul>

          <h3 className="text-xl font-semibold mb-3 mt-6">
            2.6 Privacy Violations
          </h3>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
            <li>
              Images containing faces of real people without their consent
              (biometric data under GDPR)
            </li>
            <li>Private or confidential information (doxxing)</li>
            <li>Surveillance or stalking imagery</li>
          </ul>

          <h3 className="text-xl font-semibold mb-3 mt-6">
            2.7 Spam and Malicious Use
          </h3>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
            <li>Automated or bulk generation for spam purposes</li>
            <li>Phishing or social engineering content</li>
            <li>Malware, viruses, or exploits</li>
            <li>Content designed to game algorithms or manipulate systems</li>
          </ul>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">
            3. Responsible AI Use Guidelines
          </h2>

          <h3 className="text-xl font-semibold mb-3 mt-6">3.1 Transparency</h3>
          <p className="text-muted-foreground leading-relaxed mb-4">
            When sharing AI-generated images publicly, you should:
          </p>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
            <li>
              Disclose that images are AI-generated (required under EU AI Act)
            </li>
            <li>
              Not present AI-generated content as authentic photography or
              reality
            </li>
            <li>
              Be transparent when using AI for professional or commercial
              purposes
            </li>
          </ul>

          <h3 className="text-xl font-semibold mb-3 mt-6">
            3.2 Respect for Individuals
          </h3>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
            <li>
              Obtain consent before generating or editing images of real,
              identifiable people
            </li>
            <li>
              Do not create non-consensual sexual or compromising images of real
              people
            </li>
            <li>Respect individuals' rights to their image and likeness</li>
          </ul>

          <h3 className="text-xl font-semibold mb-3 mt-6">
            3.3 Intellectual Property
          </h3>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
            <li>
              Do not attempt to recreate copyrighted characters, logos, or
              branded content
            </li>
            <li>Respect artists' and creators' original works</li>
            <li>
              Do not use AI to circumvent licensing or attribution requirements
            </li>
          </ul>

          <h3 className="text-xl font-semibold mb-3 mt-6">
            3.4 Ethical Considerations
          </h3>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
            <li>
              Consider the potential impact and consequences of generated
              content
            </li>
            <li>Use AI responsibly and for legitimate creative purposes</li>
            <li>Do not use AI to cause harm, deceive, or manipulate others</li>
          </ul>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">4. Platform Abuse</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            You may NOT:
          </p>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
            <li>
              Share, sell, or transfer your account credentials or subscription
              to others
            </li>
            <li>
              Use automated systems, bots, or scripts to abuse the service or
              generate excessive requests
            </li>
            <li>
              Attempt to reverse-engineer, decompile, or extract our AI models
            </li>
            <li>
              Circumvent rate limits, credit restrictions, or other technical
              limitations
            </li>
            <li>
              Use the service to train competing AI models or services without
              authorization
            </li>
            <li>
              Interfere with or disrupt our infrastructure or other users'
              access
            </li>
          </ul>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">
            5. Monitoring and Enforcement
          </h2>

          <h3 className="text-xl font-semibold mb-3 mt-6">
            5.1 Automated Detection
          </h3>
          <p className="text-muted-foreground leading-relaxed mb-4">
            We may use automated systems to detect violations of this policy,
            including:
          </p>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
            <li>Content filtering and moderation</li>
            <li>Pattern detection for prohibited prompts</li>
            <li>Abuse detection systems</li>
          </ul>

          <h3 className="text-xl font-semibold mb-3 mt-6">5.2 Human Review</h3>
          <p className="text-muted-foreground leading-relaxed mb-4">
            We reserve the right to review content if:
          </p>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
            <li>Automated systems flag potential violations</li>
            <li>We receive reports from other users or third parties</li>
            <li>We have reason to believe violations have occurred</li>
          </ul>

          <h3 className="text-xl font-semibold mb-3 mt-6">
            5.3 Privacy Protections
          </h3>
          <p className="text-muted-foreground leading-relaxed">
            When reviewing content, we follow strict privacy protocols and only
            access the minimum necessary information to investigate potential
            violations. See our{" "}
            <Link to="/privacy" className="text-foreground hover:underline">
              Privacy Policy
            </Link>{" "}
            for details.
          </p>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">
            6. Reporting Violations
          </h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            If you encounter content or behavior that violates this policy,
            please report it to us immediately:
          </p>
          <div className="bg-muted/50 border border-border rounded-2xl p-6">
            <p className="mb-2">
              <strong>Email:</strong>{" "}
              <a
                href="mailto:hey@harshil.dev"
                className="text-foreground hover:underline"
              >
                hey@harshil.dev
              </a>
            </p>
            <p className="mt-4 text-sm text-muted-foreground">
              Please include as much detail as possible:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4 text-sm mt-2">
              <li>Description of the violation</li>
              <li>Link to the content or user account (if applicable)</li>
              <li>Any supporting evidence or context</li>
            </ul>
          </div>
          <p className="text-muted-foreground leading-relaxed mt-4">
            We will investigate all reports promptly and take appropriate
            action. We may not be able to provide specific details about actions
            taken due to privacy considerations.
          </p>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">
            7. Consequences of Violations
          </h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Depending on the severity and frequency of violations, we may take
            the following actions:
          </p>

          <div className="space-y-4">
            <div className="bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800 rounded-2xl p-6">
              <h3 className="font-semibold mb-2">Warning</h3>
              <p className="text-muted-foreground text-sm">
                First-time or minor violations may result in a warning with
                guidance on policy compliance.
              </p>
            </div>

            <div className="bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-800 rounded-2xl p-6">
              <h3 className="font-semibold mb-2">Content Removal</h3>
              <p className="text-muted-foreground text-sm">
                Violating content will be removed from our platform.
              </p>
            </div>

            <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-2xl p-6">
              <h3 className="font-semibold mb-2">Account Suspension</h3>
              <p className="text-muted-foreground text-sm">
                Repeated or serious violations may result in temporary account
                suspension (7-30 days).
              </p>
            </div>

            <div className="bg-red-100 dark:bg-red-950/40 border border-red-300 dark:border-red-700 rounded-2xl p-6">
              <h3 className="font-semibold mb-2">Account Termination</h3>
              <p className="text-muted-foreground text-sm">
                Severe or repeated violations will result in permanent account
                termination without refund. This includes illegal content, child
                exploitation, or egregious violations.
              </p>
            </div>

            <div className="bg-purple-50 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-800 rounded-2xl p-6">
              <h3 className="font-semibold mb-2">Legal Action</h3>
              <p className="text-muted-foreground text-sm">
                Illegal activities (CSAM, terrorism, serious criminal content)
                will be reported to appropriate law enforcement authorities in
                accordance with German and EU law.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">8. Appeals</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            If you believe we made a mistake in enforcing this policy, you may
            appeal by contacting us at{" "}
            <a
              href="mailto:hey@harshil.dev"
              className="text-foreground hover:underline"
            >
              hey@harshil.dev
            </a>
            .
          </p>
          <p className="text-muted-foreground leading-relaxed">
            Please include your account information and a detailed explanation
            of why you believe the enforcement action was incorrect. We will
            review appeals promptly and respond within 7 business days.
          </p>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">
            9. Changes to This Policy
          </h2>
          <p className="text-muted-foreground leading-relaxed">
            We may update this Acceptable Use Policy as AI technology evolves
            and new risks emerge. Changes will be communicated through our
            website and, for significant updates, via email. Continued use of
            our services after changes take effect constitutes acceptance of the
            updated policy.
          </p>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">
            10. Contact Information
          </h2>
          <div className="bg-muted/50 border border-border rounded-2xl p-6">
            <p className="mb-4">
              For questions about this policy or to report violations:
            </p>
            <p>
              <strong>Email:</strong>{" "}
              <a
                href="mailto:hey@harshil.dev"
                className="text-foreground hover:underline"
              >
                hey@harshil.dev
              </a>
            </p>
          </div>
        </div>
      </section>
    </LegalLayout>
  );
}
